import type { FC } from "react";
import { useCallback, useState } from "react";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { Button } from "@/components/flexkit/Button";
import type { ServiceAccountHeaders } from "@/api";
import { useListServiceAccountSecrets } from "@/queries/service-account-secret-queries";
import { ServiceAccountSecretsTable } from "@/components/settings/organization/service-accounts/ServiceAccountSecretsTable";
import { ServiceAccountNewSecretModal } from "@/components/settings/organization/service-accounts/ServiceAccountNewSecretModal";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";

export type ServiceAccountSecretsListProps = {
    headers?: ServiceAccountHeaders;
    serviceAccountId: string;
};

export const ServiceAccountSecretsList: FC<ServiceAccountSecretsListProps> = ({ headers, serviceAccountId }) => {
    const [showNewSecretModal, setShowNewSecretModal] = useState(false);

    const { data, isPending } = useListServiceAccountSecrets({
        serviceAccountId,
        headers,
        enabled: !!headers,
    });

    const secrets = data?.secrets ?? [];

    const onNewSecret = useCallback(() => {
        setShowNewSecretModal(true);
    }, []);

    if (isPending || !headers) {
        return <SkeletonBlock ready={false} failed={false} className="h-[100px] w-full" />;
    }

    if (secrets.length === 0) {
        return (
            <>
                <EmptyStateCard
                    title="No secrets"
                    description="Add secrets that will be available as environment variables in environments created by this service account."
                    data-testid="service-account-secrets-empty-state"
                >
                    <Button
                        onClick={onNewSecret}
                        data-testid="add-service-account-secret-button"
                        data-tracking-id="new-secret-service-account-empty-state"
                    >
                        New Secret
                    </Button>
                </EmptyStateCard>
                <ServiceAccountNewSecretModal
                    open={showNewSecretModal}
                    onClose={() => setShowNewSecretModal(false)}
                    serviceAccountId={serviceAccountId}
                    headers={headers}
                />
            </>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            <div className="flex justify-end">
                <Button
                    size="sm"
                    onClick={onNewSecret}
                    data-testid="add-service-account-secret-button"
                    data-tracking-id="new-secret-service-account-list"
                >
                    New Secret
                </Button>
            </div>
            <ServiceAccountSecretsTable secrets={secrets} serviceAccountId={serviceAccountId} headers={headers} />
            <ServiceAccountNewSecretModal
                open={showNewSecretModal}
                onClose={() => setShowNewSecretModal(false)}
                serviceAccountId={serviceAccountId}
                headers={headers}
            />
        </div>
    );
};
